<script setup lang="ts">
import { BarMitzvahType, PracticeRecordingType } from 'types/types'
import { computed, onMounted, reactive, ref, toRefs } from 'vue'
import { useLogger } from 'vue-logger-plugin'

import EmptyStateCard from '@/components/EmptyStateCard.vue'
import PlaybackOverlay from '@/components/PlaybackOverlay.vue'
import StudentSelect from '@/components/StudentSelect.vue'
import { formattedDate, practiceRecordingDescription } from '@/composables/format'
import { useUtil } from '@/composables/util'
import store from '@/store'

const logger = useLogger()
const { loginCheck } = useUtil(logger)

const overlayRef = ref<InstanceType<typeof PlaybackOverlay>>()

const state = reactive<{ selectedBarMitzvah: BarMitzvahType | null }>({
  selectedBarMitzvah: null,
})

const { selectedBarMitzvah } = toRefs(state)

const sharedRecordings = computed(() => store.getters.practiceRecordings.sharedRecordings)
const loading = computed(() => store.getters.practiceRecordings.loading)
const studentName = computed(() => {
  const s = state.selectedBarMitzvah?.student
  return s ? `${s.firstName} ${s.lastName}`.trim() : undefined
})

function onRowClick(_event: Event, row: { item: PracticeRecordingType }) {
  overlayRef.value?.openOverlay(row.item)
}

function onStudentSelected(barMitzvah: BarMitzvahType | null) {
  console.log('onStudentSelected', barMitzvah ? `student=${barMitzvah.student.id} (${barMitzvah.student.firstName})` : 'null')
  state.selectedBarMitzvah = barMitzvah
  overlayRef.value?.closeOverlay()
  store.dispatch.practiceRecordings.clearSharedRecordings()
  if (barMitzvah) {
    console.log('fetchSharedRecordings for studentId:', barMitzvah.student.id)
    store.dispatch.practiceRecordings.fetchSharedRecordings(barMitzvah.student.id)
  }
}

const headers = [
  { key: 'created', sortable: true, title: 'Date' },
  { key: 'description', sortable: false, title: 'Practice' },
  { key: 'notes', sortable: false, title: 'Notes' },
  { key: 'actions', sortable: false, title: 'Actions' },
]

onMounted(() => {
  loginCheck()
  store.dispatch.teacher.fetchLightBarMitzvot()
})
</script>

<template>
  <v-container>
    <h2 class="text-h5 mb-4">Student Recordings</h2>

    <v-row>
      <v-col md="4">
        <student-select
          v-model:bar-mitzvah="selectedBarMitzvah"
          :bar-mitzvah-clearable="true"
          :external-set="true"
          @change="onStudentSelected"
        />
      </v-col>
    </v-row>

    <v-progress-linear v-if="loading" class="mt-4" color="primary" indeterminate />

    <template v-if="selectedBarMitzvah">
      <v-data-table
        v-if="sharedRecordings.length"
        class="mt-4 cursor-pointer"
        density="comfortable"
        :headers="headers"
        item-value="id"
        :items="sharedRecordings"
        :items-per-page="25"
        @click:row="onRowClick"
      >
        <template #item.created="{ item }">
          <v-chip v-if="!item.listenedAt" class="mr-2" color="info" size="x-small">New</v-chip>
          {{ formattedDate(item.created) }}
        </template>

        <template #item.description="{ item }">
          {{ practiceRecordingDescription(item) }}
        </template>

        <template #item.notes="{ item }">
          <span class="text-body-2">{{ item.notes || '—' }}</span>
        </template>

        <template #item.actions="{ item }">
          <v-btn color="primary" icon="mdi-play" variant="text" @click="overlayRef?.openOverlay(item)" />
          <v-btn color="primary" icon="mdi-download" variant="text" @click="overlayRef?.downloadRecording(item)" />
        </template>
      </v-data-table>

      <empty-state-card
        v-else-if="!loading"
        card-class="mt-4"
        description="This student hasn't shared any recordings yet."
        icon="mdi-microphone-off"
        title="No shared recordings"
      />
    </template>

    <empty-state-card
      v-else-if="!loading"
      card-class="mt-4"
      description="Select a student to see their shared recordings."
      icon="mdi-account-music"
      title="Select a student"
    />

    <playback-overlay ref="overlayRef" :bar-mitzvah="selectedBarMitzvah" :student-name="studentName" />
  </v-container>
</template>
